<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import CanalProAPI from 'dashboard/api/canalpro';
import CanalProPageState from '../components/CanalProPageState.vue';
import {
  collectionFromResponse,
  errorMessage,
  recordFromResponse,
} from '../utils/canalproData';
import {
  createListingForm,
  listingCanBeSavedAsDraft,
  listingIsComplete,
  listingPayloadFromForm,
} from '../utils/listingPayload';

const route = useRoute();
const router = useRouter();
const accounts = ref([]);
const loading = ref(true);
const error = ref('');
const saving = ref(false);
const uploading = ref(false);
const amenityInput = ref('');
const accountIsLocked = ref(false);
const form = reactive(createListingForm());

const isEditing = computed(() => Boolean(route.params.id));
const selectedAccountName = computed(
  () =>
    accounts.value.find(
      item => String(item.id) === String(form.canalproAccountId)
    )?.name || 'Escolha a conta CanalPro'
);
const descriptionLength = computed(() => form.description.trim().length);
const imagePreview = image =>
  typeof image === 'string'
    ? image
    : image?.url ||
      image?.image_url ||
      image?.imageUrl ||
      image?.resized_url ||
      image?.resizedUrl ||
      '';

const copyForm = source => {
  const normalized = createListingForm(source);
  Object.assign(form, normalized);
};

const loadPage = async () => {
  loading.value = true;
  error.value = '';
  try {
    const accountsResponse = await CanalProAPI.accounts();
    accounts.value = collectionFromResponse(accountsResponse);
    if (isEditing.value) {
      const listing = recordFromResponse(
        await CanalProAPI.listing(route.params.id)
      );
      accountIsLocked.value = Boolean(listing.remote_id);
      copyForm(listing);
    } else {
      form.canalproAccountId = route.query.account_id || '';
    }
  } catch (requestError) {
    error.value = errorMessage(
      requestError,
      'Não foi possível abrir este anúncio.'
    );
  } finally {
    loading.value = false;
  }
};

const addAmenity = () => {
  const value = amenityInput.value.trim();
  if (!value || form.amenities.includes(value)) return;
  form.amenities.push(value);
  amenityInput.value = '';
};

const removeAmenity = amenity => {
  form.amenities = form.amenities.filter(value => value !== amenity);
};

const uploadPhotos = async event => {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  if (!form.canalproAccountId) {
    useAlert('Escolha primeiro a conta onde este anúncio será publicado.');
    event.target.value = '';
    return;
  }

  uploading.value = true;
  try {
    const uploaded = await Promise.all(
      files.map(async file =>
        recordFromResponse(
          await CanalProAPI.uploadListingMedia(file, form.canalproAccountId)
        )
      )
    );
    form.images.push(...uploaded.filter(Boolean));
  } catch (requestError) {
    useAlert(
      errorMessage(requestError, 'Não foi possível enviar uma ou mais fotos.')
    );
  } finally {
    uploading.value = false;
    event.target.value = '';
  }
};

const removeImage = index => form.images.splice(index, 1);

const save = async publish => {
  if (publish && !listingIsComplete(form)) {
    useAlert(
      'Preencha a conta, o título, a descrição, a cidade, o estado e o valor do imóvel.'
    );
    return;
  }
  if (!publish && !listingCanBeSavedAsDraft(form)) {
    useAlert(
      'Escolha uma conta e informe ao menos o título e a descrição para salvar o rascunho.'
    );
    return;
  }
  if (publish && descriptionLength.value < 80) {
    useAlert(
      'Para publicar, conte um pouco mais sobre o imóvel: use pelo menos 80 caracteres na descrição.'
    );
    return;
  }
  if (publish && !form.images.length) {
    useAlert('Adicione pelo menos uma foto para publicar este anúncio.');
    return;
  }

  saving.value = true;
  try {
    const payload = listingPayloadFromForm(form);
    const syncWithGandalf =
      publish || (isEditing.value && accountIsLocked.value);
    const response = isEditing.value
      ? await (syncWithGandalf
          ? CanalProAPI.updateLiveListing(route.params.id, payload)
          : CanalProAPI.updateListing(route.params.id, payload))
      : await (publish
          ? CanalProAPI.createLiveListing(payload)
          : CanalProAPI.createListing(payload));
    const listing = recordFromResponse(response);
    if (!listing.id && !route.params.id) {
      throw new Error('O CanalPro não confirmou o anúncio criado.');
    }
    useAlert(publish ? 'Anúncio publicado.' : 'Rascunho salvo.');
    router.push({
      name: 'canalpro_listings_index',
      query: {
        account_id: form.canalproAccountId,
        ...(publish ? {} : { status: 'draft' }),
      },
    });
  } catch (requestError) {
    useAlert(
      errorMessage(
        requestError,
        publish
          ? 'Não foi possível publicar este anúncio.'
          : 'Não foi possível salvar o rascunho.'
      )
    );
  } finally {
    saving.value = false;
  }
};

onMounted(loadPage);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main
    class="h-full min-h-0 overflow-y-auto overscroll-contain bg-slate-50 px-4 py-6 sm:px-6 lg:px-10"
  >
    <CanalProPageState
      v-if="loading || error"
      :loading="loading"
      :error="error"
      @retry="loadPage"
    />
    <section v-else class="mx-auto max-w-6xl">
      <header
        class="mb-6 flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <button
            class="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
            type="button"
            @click="router.back()"
          >
            <span class="i-lucide-arrow-left" /> Voltar aos anúncios
          </button>
          <p class="text-sm font-medium text-indigo-600">
            {{ selectedAccountName }}
          </p>
          <h1 class="mt-1 text-2xl font-semibold text-slate-900">
            {{ isEditing ? 'Editar anúncio' : 'Criar anúncio' }}
          </h1>
          <p class="mt-2 text-sm text-slate-600">
            Mostre o imóvel de um jeito claro. Você pode salvar agora e publicar
            quando estiver tudo certo.
          </p>
        </div>
        <div class="flex gap-2">
          <button
            class="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            type="button"
            :disabled="saving"
            @click="save(false)"
          >
            Salvar rascunho
          </button>
          <button
            class="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
            type="button"
            :disabled="saving"
            @click="save(true)"
          >
            {{ saving ? 'Salvando…' : 'Publicar agora' }}
          </button>
        </div>
      </header>

      <form
        class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]"
        @submit.prevent="save(false)"
      >
        <section class="space-y-6">
          <div
            class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div class="flex items-center justify-between gap-3">
              <div>
                <h2 class="font-semibold text-slate-900">Fotos do imóvel</h2>
                <p class="mt-1 text-sm text-slate-500">
                  A primeira foto aparece como capa do anúncio.
                </p>
              </div>
              <label
                class="cursor-pointer rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                ><input
                  class="sr-only"
                  type="file"
                  accept="image/*"
                  multiple
                  @change="uploadPhotos"
                />{{ uploading ? 'Enviando…' : 'Adicionar fotos' }}</label
              >
            </div>
            <div
              v-if="form.images.length"
              class="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3"
            >
              <figure
                v-for="(image, index) in form.images"
                :key="image.id || image.url || index"
                class="group relative aspect-square overflow-hidden rounded-lg bg-slate-100"
              >
                <img
                  v-if="imagePreview(image)"
                  :src="imagePreview(image)"
                  :alt="`Foto ${index + 1} do imóvel`"
                  class="h-full w-full object-cover"
                />
                <button
                  class="absolute right-2 top-2 rounded-full bg-slate-950/70 p-1.5 text-white opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100"
                  type="button"
                  :aria-label="`Remover foto ${index + 1}`"
                  @click="removeImage(index)"
                >
                  <span class="i-lucide-x text-sm" />
                </button>
                <figcaption
                  v-if="index === 0"
                  class="absolute bottom-2 left-2 rounded bg-white/95 px-2 py-1 text-xs font-semibold text-slate-800"
                >
                  Capa
                </figcaption>
              </figure>
            </div>
            <div
              v-else
              class="mt-5 flex min-h-44 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-center text-slate-500"
            >
              <span class="i-lucide-images mb-2 text-3xl" />
              <p class="text-sm font-medium">
                Adicione fotos para valorizar o imóvel
              </p>
            </div>
          </div>

          <div
            class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h2 class="font-semibold text-slate-900">Sobre o imóvel</h2>
            <div class="mt-5 grid gap-4">
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Conta onde será publicado<select
                  v-model="form.canalproAccountId"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-slate-100"
                  :disabled="accountIsLocked"
                >
                  <option value="">Escolha uma conta</option>
                  <option
                    v-for="account in accounts"
                    :key="account.id"
                    :value="account.id"
                  >
                    {{
                      account.name || account.business_name || 'Conta CanalPro'
                    }}
                  </option></select
                ><span
                  v-if="accountIsLocked"
                  class="text-xs font-normal text-slate-500"
                  >A conta fica bloqueada após a primeira sincronização para
                  evitar publicação no perfil errado.</span
                ></label
              >
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Título do anúncio<input
                  v-model="form.title"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  maxlength="100"
                  placeholder="Ex.: Apartamento com varanda e vista livre"
              /></label>
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Descrição<textarea
                  v-model="form.description"
                  class="min-h-36 rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  maxlength="5000"
                  placeholder="Conte os pontos que tornam este imóvel especial: ambientes, localização, acabamentos e diferenciais."
                />
              </label>
              <p
                class="-mt-2 text-right text-xs"
                :class="
                  descriptionLength >= 80
                    ? 'text-emerald-600'
                    : 'text-slate-500'
                "
              >
                {{ descriptionLength }} caracteres{{
                  descriptionLength < 80
                    ? ' · use pelo menos 80 para publicar'
                    : ''
                }}
              </p>
              <div class="grid gap-4 sm:grid-cols-3">
                <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                  >Finalidade<select
                    v-model="form.businessType"
                    class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="SALE">Venda</option>
                    <option value="RENTAL">Aluguel</option>
                  </select></label
                >
                <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                  >Tipo de imóvel<select
                    v-model="form.propertyType"
                    class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="APARTMENT">Apartamento</option>
                    <option value="HOUSE">Casa</option>
                    <option value="CONDOMINIUM">Casa em condomínio</option>
                    <option value="COMMERCIAL">Comercial</option>
                    <option value="LAND">Terreno</option>
                    <option value="STUDIO">Studio</option>
                  </select></label
                >
                <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                  >Tipo de anúncio<select
                    v-model="form.publicationType"
                    class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  >
                    <option value="STANDARD">Padrão</option>
                    <option value="PREMIERE_1">Exclusivo</option>
                    <option value="PREMIERE_2">Superior</option>
                  </select></label
                >
              </div>
            </div>
          </div>

          <div
            class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h2 class="font-semibold text-slate-900">Localização</h2>
            <div class="mt-5 grid gap-4 sm:grid-cols-2">
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >CEP<input
                  v-model="form.zipCode"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  inputmode="numeric"
                  placeholder="00000-000"
              /></label>
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Estado<select
                  v-model="form.state"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="">Escolha</option>
                  <option
                    v-for="state in [
                      'AC',
                      'AL',
                      'AP',
                      'AM',
                      'BA',
                      'CE',
                      'DF',
                      'ES',
                      'GO',
                      'MA',
                      'MT',
                      'MS',
                      'MG',
                      'PA',
                      'PB',
                      'PR',
                      'PE',
                      'PI',
                      'RJ',
                      'RN',
                      'RS',
                      'RO',
                      'RR',
                      'SC',
                      'SP',
                      'SE',
                      'TO',
                    ]"
                    :key="state"
                    :value="state"
                  >
                    {{ state }}
                  </option>
                </select></label
              >
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Cidade<input
                  v-model="form.city"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
              /></label>
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Bairro<input
                  v-model="form.neighborhood"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
              /></label>
              <label
                class="grid gap-1.5 text-sm font-medium text-slate-700 sm:col-span-2"
                >Rua ou avenida<input
                  v-model="form.street"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
              /></label>
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Número<input
                  v-model="form.streetNumber"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
              /></label>
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Complemento<input
                  v-model="form.complement"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Ex.: ap. 32"
              /></label>
            </div>
            <label class="mt-4 flex items-center gap-2 text-sm text-slate-700"
              ><input
                v-model="form.displayAddress"
                class="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                type="checkbox"
              />
              Mostrar o endereço no anúncio</label
            >
          </div>
        </section>

        <aside class="space-y-6">
          <section
            class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h2 class="font-semibold text-slate-900">Valores</h2>
            <div class="mt-5 grid gap-4">
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Valor do imóvel<input
                  v-model="form.price"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  inputmode="decimal"
                  placeholder="Ex.: 850000"
              /></label>
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Condomínio mensal<input
                  v-model="form.monthlyCondoFee"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  inputmode="decimal"
                  placeholder="Opcional"
              /></label>
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >IPTU anual<input
                  v-model="form.yearlyIptu"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  inputmode="decimal"
                  placeholder="Opcional"
              /></label>
              <label class="flex items-center gap-2 text-sm text-slate-700"
                ><input
                  v-model="form.showPrice"
                  class="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  type="checkbox"
                />
                Mostrar valor no anúncio</label
              >
            </div>
          </section>
          <section
            class="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h2 class="font-semibold text-slate-900">Características</h2>
            <div class="mt-5 grid grid-cols-2 gap-3">
              <label class="grid gap-1 text-sm font-medium text-slate-700"
                >Quartos<input
                  v-model="form.bedrooms"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  type="number"
                  min="0"
              /></label>
              <label class="grid gap-1 text-sm font-medium text-slate-700"
                >Banheiros<input
                  v-model="form.bathrooms"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  type="number"
                  min="0"
              /></label>
              <label class="grid gap-1 text-sm font-medium text-slate-700"
                >Suítes<input
                  v-model="form.suites"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  type="number"
                  min="0"
              /></label>
              <label class="grid gap-1 text-sm font-medium text-slate-700"
                >Vagas<input
                  v-model="form.parkingSpaces"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  type="number"
                  min="0"
              /></label>
              <label class="grid gap-1 text-sm font-medium text-slate-700"
                >Área útil m²<input
                  v-model="form.usableArea"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  inputmode="decimal"
              /></label>
              <label class="grid gap-1 text-sm font-medium text-slate-700"
                >Área total m²<input
                  v-model="form.totalArea"
                  class="rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                  inputmode="decimal"
              /></label>
            </div>
            <div class="mt-5">
              <label class="grid gap-1.5 text-sm font-medium text-slate-700"
                >Diferenciais
                <div class="flex gap-2">
                  <input
                    v-model="amenityInput"
                    class="min-w-0 flex-1 rounded-lg border-slate-300 focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="Ex.: Piscina"
                    @keydown.enter.prevent="addAmenity"
                  /><button
                    class="rounded-lg bg-slate-100 px-3 text-sm font-semibold text-slate-700 hover:bg-slate-200"
                    type="button"
                    @click="addAmenity"
                  >
                    Adicionar
                  </button>
                </div></label
              >
              <div
                v-if="form.amenities.length"
                class="mt-3 flex flex-wrap gap-2"
              >
                <button
                  v-for="amenity in form.amenities"
                  :key="amenity"
                  class="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                  type="button"
                  @click="removeAmenity(amenity)"
                >
                  {{ amenity }} <span class="i-lucide-x text-xs" />
                </button>
              </div>
            </div>
          </section>
        </aside>
      </form>
    </section>
  </main>
</template>
